import { useCallback, useMemo } from "react";

/**
 * Generic hook for managing per-student state from a Map-based context
 * @param {string} studentId - The ID of the current student
 * @param {Map} globalState - The global Map state (e.g., allUpdated, allAdded)
 * @param {Function} setGlobalState - The setter function for the global Map state
 * @param {Map|Set} defaultValue - The default value if student state doesn't exist (Map or Set)
 * @returns {[any, Function]} - Returns [currentStudentState, setCurrentStudentState]
 */
const useStudentState = (
  studentId,
  globalState,
  setGlobalState,
  defaultValue
) => {
  const currentStudentState = useMemo(() => {
    return globalState.get(studentId) || defaultValue;
  }, [globalState, studentId, defaultValue]);

  const setCurrentStudentState = useCallback(
    updater => {
      setGlobalState(prev => {
        const newState = new Map(prev);
        const studentState =
          defaultValue instanceof Map
            ? new Map(newState.get(studentId) || [])
            : new Set(newState.get(studentId) || []);

        updater(studentState);
        newState.set(studentId, studentState);
        return newState;
      });
    },
    [setGlobalState, studentId, defaultValue]
  );

  return [currentStudentState, setCurrentStudentState];
};

export default useStudentState;
